import { useEffect, useState } from 'react';
import { Button } from '../../ui';

/**
 * The one place this product shows an invitation link on a screen.
 *
 * It exists so the client half of the invitation flow can be walked through on
 * the deployed site, where a `.test` address can be invited but its mail goes
 * nowhere real (change `deployed-invite-unblock`). It is scaffolding: the gate
 * in `docs/runbooks/before-the-first-real-client.md` requires it to be gone
 * before the first real person has an account.
 *
 * **A link only ever appears here for an address that cannot belong to a
 * person.** The server decides that — `mayRevealInviteLink` in
 * `supabase/functions/_shared/environment.ts` returns the link only outside
 * production, only for a domain ending in `.test`, and only behind an explicit
 * flag — and this component checks the address again before it renders
 * anything. The second check is not redundant: it means no future response
 * shape, no mocked call and no misread flag can put a real person's sign-in
 * credential on the coach's screen.
 *
 * Nothing was mailed for a link that appears here: the reveal is the delivery,
 * decided once before the invitation was minted (design.md D8). The panel says
 * so, because a coach who believes an email is on its way is exactly the
 * failure `supabase/functions/_shared/mail.ts` exists to prevent.
 *
 * The link is rendered as **text**, never as an anchor and never opened with
 * `window.open`, so following it cannot write it into browser history. It lives
 * in component state and nowhere else — not in the query cache, not in storage
 * — so a reload or leaving the screen is the end of it, and the only way back
 * to a working link is "Resend invitation", which mints a fresh one and kills
 * the old.
 */

/**
 * The sentence that keeps the coach from waiting for a message that is not
 * coming.
 *
 * Where the link is shown, nothing was handed to a mail transport at all — the
 * reveal *is* the delivery (design.md D8). `mail.ts` was written against a
 * coach believing an invitation is on its way when it is not, and a silent
 * non-send reaches that belief just as surely as a silent failure does, so the
 * panel says it outright.
 */
export const DEMO_LINK_NO_EMAIL =
  'No email was sent to this address — this link is the only way in.';

/** Verbatim from the spec. The panel is deliberately impossible to mistake. */
export const DEMO_LINK_WARNING =
  'Demo link — this environment accepts only undeliverable .test addresses. ' +
  "Never paste a real person's address here.";

/** RFC 2606 reserves `.test`; no resolver will ever answer for it. */
export function cannotReceiveMail(email: string): boolean {
  const address = email.trim().toLowerCase();
  return address.includes('@') && address.slice(address.lastIndexOf('@') + 1).endsWith('.test');
}

export interface RevealedLink {
  email: string;
  link: string;
}

/**
 * Hold at most one revealed link, for as long as the screen that minted it.
 *
 * `reveal` refuses anything it should not hold, so the refusal does not depend
 * on every caller remembering to ask. The cleanup drops it on unmount, which is
 * what makes "the link is not there a second time" true rather than merely
 * likely.
 */
export function useRevealedLink(): {
  revealed: RevealedLink | null;
  reveal: (email: string, link: string | undefined) => void;
  clear: () => void;
} {
  const [revealed, setRevealed] = useState<RevealedLink | null>(null);

  useEffect(
    () => () => {
      setRevealed(null);
    },
    [],
  );

  return {
    revealed,
    clear: () => {
      setRevealed(null);
    },
    reveal: (email, link) => {
      setRevealed(
        link !== undefined && link !== '' && cannotReceiveMail(email) ? { email, link } : null,
      );
    },
  };
}

export function DemoInviteLink({ revealed }: { revealed: RevealedLink | null }) {
  // The note is stored against the link it belongs to, rather than reset by an
  // effect, so a fresh link never inherits the previous one's "Copied."
  const [copied, setCopied] = useState<{ link: string; note: string } | null>(null);

  // The same rule as the hook, applied at the moment of rendering: a link is on
  // screen only if the address it belongs to can reach no mailbox.
  if (revealed === null || !cannotReceiveMail(revealed.email)) return null;

  const link = revealed.link;
  const note = copied?.link === link ? copied.note : null;

  const failed = () => {
    setCopied({ link, note: 'Could not copy. Select the link above instead.' });
  };

  const copy = () => {
    // Not every context has a clipboard — an insecure origin does not, and
    // reading `navigator.clipboard` there throws rather than returning
    // undefined. The link is selectable text either way, so a failure says what
    // to do instead of leaving the coach clicking a dead button.
    try {
      void navigator.clipboard
        .writeText(link)
        .then(() => {
          setCopied({ link, note: 'Copied.' });
        })
        .catch(failed);
    } catch {
      failed();
    }
  };

  return (
    <div
      data-testid="demo-invite-link"
      className="border-attention bg-attention-tint rounded-[--radius-card] border p-4"
    >
      <p role="alert" className="text-attention text-sm font-medium">
        {DEMO_LINK_WARNING}
      </p>

      <p className="text-ink-soft mt-2 text-sm">{DEMO_LINK_NO_EMAIL}</p>

      <p
        data-testid="demo-invite-link-value"
        className="text-ink-soft mt-3 font-mono text-xs break-all select-all"
      >
        {revealed.link}
      </p>

      <div className="mt-3 flex items-center gap-3">
        <Button variant="secondary" onClick={copy}>
          Copy
        </Button>
        {note === null ? null : (
          <span role="status" className="text-ink-muted text-xs">
            {note}
          </span>
        )}
      </div>
    </div>
  );
}
